import {
    useEffect,
    useState
} from "react";
import {useAuth} from "../../../../hooks/useAuth.ts";
import {
    Navigate,
    useNavigate,
    useSearchParams
} from "react-router-dom";
import {
    FlexiSignInButton,
    PlatformLoader
} from "@flexinet/ui-components";
import {
    Center,
    Stack,
    Title
} from "@mantine/core";

import {Cache} from 'aws-amplify/utils';
import {isAllowedRedirectUrl} from "../../../../utils/isAllowedRedirectUrl.ts";
import {
    MintAlreadyInFlightError,
    mintAuthorizationCode
} from "../../../../utils/mintAuthorizationCode.ts";
import {isAuthBrokerAvailable} from "../../../../config/authBroker.ts";
import {loadFreshSession} from "../../../../utils/loadFreshSession.ts";

export const AuthorizePage = () => {

    const navigate = useNavigate()

    const [params] = useSearchParams();

    const redirectUrl = params.get('redirect_uri')
    const responseType = params.get('response_type')
    const clientId = params.get('client_id')
    const identityProvider = params.get('identity_provider')
    const scope = params.get('scope')
    const state = params.get('state')

    // NOTE: the PKCE challenge is deliberately NOT read here. On the code path
    // the broker parked it at the start of the flow and binds the code to its
    // own copy — the portal never handles it, and could not be trusted to
    // assert it even if it did.

    // FNP-864: validate BEFORE caching. This component renders for signed-out
    // visitors too, so an unvalidated write here lets an attacker seed the
    // cache with their own origin and have the post-sign-in emitter deliver
    // tokens to it.
    const redirectUrlIsAllowed = isAllowedRedirectUrl(redirectUrl, responseType)

    useEffect(() => {
        if (!redirectUrlIsAllowed) {
            // Fail closed: never persist an unknown redirect target, and drop
            // any entry a previous request may have left behind.
            Cache.removeItem('authorization')
            return
        }

        Cache.setItem('authorization', {
            redirectUrl,
            responseType,
            clientId,
            identityProvider,
            scope,
            state
        })
    }, [redirectUrlIsAllowed, redirectUrl, responseType, clientId, identityProvider, scope, state])


    const {user} = useAuth()


    const {
        idToken,
        accessToken,
    } = useAuth();


    // FNP-871/872: is this a migrated (code-flow) app?
    const isCodeFlow = responseType === 'code'

    const [codeExchangeError, setCodeExchangeError] = useState(false)

    // ── FNP-871/872: THE CODE-FLOW HANDOFF ────────────────────────────────
    //
    // Reads the session DIRECTLY from Amplify rather than from useAuth().
    //
    // This is the fix for four rounds of failures, and the reason is worth
    // keeping: useAuth() is React context populated by two separate effects, so
    // `user`, `idToken` and `accessToken` arrive at different moments and this
    // component REMOUNTS while they settle. Every guard tied to a component
    // instance (useRef) was reset by the remount, and the mount that won the
    // race was routinely unmounted before it could navigate — so a code was
    // minted and nobody redirected. The user sat on a loader with no error and
    // a broker log that said the sign-in had SUCCEEDED.
    //
    // loadFreshSession() reads tokens straight out of Amplify's storage, after
    // forcing a refresh. No context, no hydration race, no dependency on which
    // mount is alive. The sessionStorage claim in mintAuthorizationCode still
    // makes the mint itself idempotent across mounts.
    useEffect(() => {
        if (!isCodeFlow || !redirectUrlIsAllowed || codeExchangeError) {
            return
        }
        if (!isAuthBrokerAvailable() || !state) {
            return
        }

        const run = async () => {
            try {
                // FNP-1358: refreshed against Cognito first, and carrying the
                // refresh token, so the app gets a session that outlives the
                // access token and never one that was already revoked.
                const session = await loadFreshSession()

                // No session yet — the user is signed out. Fall through and let
                // the component render the sign-in prompt below.
                if (session.status === 'no-session') {
                    return
                }

                // The portal session is dead. Sign in again; the cached request
                // survives, so the sign-in page completes the mint afterwards.
                if (session.status === 'expired') {
                    window.location.assign('/sign-in')
                    return
                }

                // A transient failure with the session intact. Signing in again
                // would not help; show the error and let the user retry.
                if (session.status === 'unavailable') {
                    setCodeExchangeError(true)
                    return
                }

                const redirectTo = await mintAuthorizationCode({
                    caller: 'AuthorizePage',
                    requestId: state,
                    idToken: session.idToken,
                    accessToken: session.accessToken,
                    refreshToken: session.refreshToken,
                })

                await Cache.removeItem('authorization')

                // Deliberately NOT guarded by a `cancelled` flag. A redirect is
                // not component state: it must happen whichever mount is alive
                // when the response lands. Guarding it here is precisely what
                // produced the silent hang.
                window.location.assign(redirectTo)
            } catch (error) {
                // Another mount already minted and is navigating — not an error.
                if (error instanceof MintAlreadyInFlightError) {
                    return
                }
                // Never log the error's payload — the request carried tokens.
                setCodeExchangeError(true)
            }
        }

        run()
    }, [isCodeFlow, redirectUrlIsAllowed, state, codeExchangeError])


    // const {
    //     customState,
    //     saveCustomState
    // } = useCustomState()

    // useEffect(() => {
    //     saveCustomState()
    // }, [])


    if (user) {

        if (typeof idToken === 'undefined' || typeof accessToken === 'undefined') {
            return <PlatformLoader/>
        }

        // FNP-864: re-validate immediately before emitting tokens. Fail closed
        // to the dashboard rather than throwing an unhandled error mid-render.
        if (!redirectUrlIsAllowed) {
            Cache.removeItem('authorization')
            return <Navigate to={'/dashboard'}/>
        }

        // ── FNP-871/872: MIGRATED APPS TAKE THE CODE PATH ──────────────────
        //
        // The discriminator is `response_type`, which Amplify already sends and
        // which this page already reads. An app migrates by flipping its own
        // `responseType` to 'code'; nothing here needs a per-app registry, and
        // no app is affected until it opts in.
        //
        //   response_type=code   -> broker mints a single-use code (migrated)
        //   response_type=token  -> raw JWTs in the fragment  (everything else)
        //
        // The fragment path below is UNCHANGED and stays live until every app
        // has migrated. Deleting it is FNP-866 step 3, not this ticket.
        if (isCodeFlow) {
            // A code-flow request the broker cannot serve must FAIL, not fall
            // back to the fragment. A silent downgrade would hand raw tokens to
            // an app that asked specifically not to receive them.
            if (!isAuthBrokerAvailable()) {
                return <Center>
                    <Stack>
                        <Title my={'xl'}>Sign-in is unavailable for this application.</Title>
                        <Center my={'xl'}>
                            <FlexiSignInButton onClick={() => navigate('/dashboard')}>
                                Back to dashboard
                            </FlexiSignInButton>
                        </Center>
                    </Stack>
                </Center>
            }

            if (codeExchangeError) {
                return <Center>
                    <Stack>
                        <Title my={'xl'}>Could not complete sign-in. Please try again.</Title>
                        <Center my={'xl'}>
                            <FlexiSignInButton onClick={() => navigate('/dashboard')}>
                                Back to dashboard
                            </FlexiSignInButton>
                        </Center>
                    </Stack>
                </Center>
            }

            // The mint + redirect runs in the effect below, so this render is
            // just the waiting state.
            return <PlatformLoader/>
        }

        // FNP-864: never log this URL — it carries both raw tokens.
        const returnUrl = `${redirectUrl}#id_token=${idToken}&access_token=${accessToken}&token_type=Bearer&expires_in=3600&state=${state}`
        Cache.removeItem('authorization')
             .then(() => {
                 window.location.assign(returnUrl)
             })

    } else {

        return (<Center>
            <Stack>
                <Title my={'xl'}>Session has expired. Please, sign in again!</Title>
                <Center my={'xl'}>
                    <FlexiSignInButton onClick={() => navigate('/')}>Sign In</FlexiSignInButton>
                </Center>
            </Stack>
        </Center>)

    }


}
